/**
 * ImportDialog — importador genérico de listados (Excel/CSV).
 *
 * POR QUÉ EXISTE
 * ---------------------------------------------------------------------------
 * El usuario pidió importador en TODAS las fichas. Copiar el diálogo de «Otros
 * periféricos» 12 veces habría dejado 12 diálogos que se rompen a la vez. Aquí
 * el esquema lo pone el backend (`GET /api/import/<entidad>/schema`) y este
 * componente solo se encarga de: leer el fichero, mapear cabeceras, previsualizar
 * y enviar las filas ya con claves canónicas.
 *
 * El fichero NO se sube: SheetJS lo lee en el navegador (ya estaba en el bundle
 * para exportar a Excel).
 *
 * TRES PASOS EN UN DIÁLOGO
 *  1. Fichero (drag & drop o selector) → se muestran las cabeceras detectadas.
 *  2. Mapeo campo ↔ columna, con vista previa de las primeras filas.
 *  3. Resultado: creados / actualizados / omitidos + errores por fila.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { ArrowUpTrayIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import Dialog from './Dialog';
import api from '../../services/api';

const norm = (value) => String(value ?? '')
  .replace(/\ufeff/g, '')
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

function textoCelda(value) {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) {
    const yyyy = value.getFullYear();
    const mm = String(value.getMonth() + 1).padStart(2, '0');
    const dd = String(value.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
  return String(value).trim();
}

export default function ImportDialog({ entity, companyId, onClose, onDone }) {
  const [schema, setSchema] = useState(null);
  const [error, setError] = useState('');
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [updateExisting, setUpdateExisting] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.getImportSchema(entity)
      .then((data) => { if (!cancelled) setSchema(data); })
      .catch((err) => { if (!cancelled) setError(err.message || 'No se pudo cargar el importador'); });
    return () => { cancelled = true; };
  }, [entity]);

  const autoMap = useCallback((fields, headerList) => {
    const byNorm = new Map(headerList.map((header) => [norm(header), header]));
    const next = {};
    fields.forEach((field) => {
      const candidates = [field.label, ...(field.aliases || [])].map(norm);
      const found = candidates.find((candidate) => byNorm.has(candidate));
      // Segundo intento: cabecera que CONTENGA el alias (p. ej. «IP de gestión»).
      const partial = !found
        ? headerList.find((header) => candidates.some((candidate) => norm(header).includes(candidate)))
        : null;
      next[field.key] = found ? byNorm.get(found) : (partial || '');
    });
    return next;
  }, []);

  const leerFichero = async (file) => {
    setError('');
    setResult(null);
    try {
      const isCsv = /\.csv$/i.test(file.name);
      let workbook;
      if (isCsv) {
        const buffer = await file.arrayBuffer();
        const text = new TextDecoder('utf-8').decode(buffer);
        const firstLine = text.split(/\r?\n/)[0] || '';
        const semicolons = (firstLine.match(/;/g) || []).length;
        const commas = (firstLine.match(/,/g) || []).length;
        const tabs = (firstLine.match(/\t/g) || []).length;
        const FS = tabs > semicolons && tabs > commas ? '\t' : (semicolons > commas ? ';' : ',');
        workbook = XLSX.read(text, { type: 'string', FS, cellDates: true, codepage: 65001 });
      } else {
        const buffer = await file.arrayBuffer();
        workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
      }
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false, defval: '' });
      if (!matrix.length) throw new Error('El fichero está vacío');
      const headerRow = matrix[0].map((cell) => textoCelda(cell)).filter(Boolean);
      const dataRows = matrix.slice(1).filter((row) => row.some((cell) => textoCelda(cell) !== ''));
      if (!dataRows.length) throw new Error('No hay filas de datos debajo de la cabecera');

      setFileName(file.name);
      setHeaders(headerRow);
      setRows(dataRows.slice(0, schema?.max_rows || 3000).map((row) => {
        const obj = {};
        headerRow.forEach((header, index) => { obj[header] = textoCelda(row[index]); });
        return obj;
      }));
      setMapping(autoMap(schema?.fields || [], headerRow));
    } catch (err) {
      setError(err.message || 'No se pudo leer el fichero');
      setHeaders([]);
      setRows([]);
    }
  };

  const previewRows = useMemo(() => {
    if (!schema) return [];
    return rows.slice(0, 6).map((row) => {
      const mapped = {};
      schema.fields.forEach((field) => {
        const header = mapping[field.key];
        if (header && row[header] !== undefined && row[header] !== '') {
          mapped[field.key] = row[header];
        }
      });
      return mapped;
    });
  }, [rows, mapping, schema]);

  const faltanObligatorios = useMemo(() => {
    if (!schema) return [];
    return schema.fields
      .filter((field) => field.required && !mapping[field.key])
      .map((field) => field.label);
  }, [schema, mapping]);

  const descargarPlantilla = () => {
    if (!schema) return;
    const cabeceras = schema.fields.map((field) => field.label);
    const ejemplo = schema.fields.map((field) => (field.required ? `Ejemplo ${field.label}` : ''));
    const sheet = XLSX.utils.aoa_to_sheet([cabeceras, ejemplo]);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Importación');
    XLSX.writeFile(book, `plantilla-${entity}.xlsx`);
  };

  const enviar = async () => {
    if (!schema) return;
    setError('');
    setSubmitting(true);
    try {
      const payload = rows.map((row) => {
        const mapped = {};
        schema.fields.forEach((field) => {
          const header = mapping[field.key];
          if (header && row[header] !== undefined && row[header] !== '') {
            mapped[field.key] = row[header];
          }
        });
        return mapped;
      }).filter((row) => Object.keys(row).length > 0);

      const resumen = await api.runImport(entity, payload, updateExisting, companyId);
      setResult(resumen);
    } catch (err) {
      setError(err.message || 'No se pudo importar');
    } finally {
      setSubmitting(false);
    }
  };

  const campos = schema?.fields || [];

  return (
    <Dialog
      title={`Importar ${schema?.label || entity}`}
      icon={<ArrowUpTrayIcon style={{ width: 18, height: 18 }} />}
      maxWidth={860}
      busy={submitting}
      onClose={onClose}
      footer={(
        <>
          {result ? (
            <>
              <button type="button" className="list-btn-secondary" onClick={() => { setResult(null); setRows([]); setHeaders([]); setFileName(''); }}>
                Importar otro fichero
              </button>
              <button type="button" className="list-btn-primary" onClick={() => onDone?.(result)}>
                Cerrar
              </button>
            </>
          ) : (
            <>
              <button type="button" className="list-btn-secondary" onClick={onClose} disabled={submitting}>
                Cancelar
              </button>
              <button
                type="button"
                className="list-btn-primary"
                onClick={enviar}
                disabled={submitting || !rows.length || faltanObligatorios.length > 0}
              >
                {submitting ? 'Importando…' : `Importar ${rows.length || ''} fila${rows.length === 1 ? '' : 's'}`}
              </button>
            </>
          )}
        </>
      )}
    >
      {!schema && !error && (
        <p className="list-hint">Cargando el importador…</p>
      )}
      {error && (
        <p style={{ color: 'var(--danger, #ef4444)', marginTop: 0 }}>{error}</p>
      )}

      {schema && !result && (
        <>
          <div
            onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              const file = event.dataTransfer.files?.[0];
              if (file) leerFichero(file);
            }}
            onClick={() => inputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => { if (event.key === 'Enter') inputRef.current?.click(); }}
            style={{
              border: `2px dashed ${dragging ? 'var(--accent)' : 'var(--border)'}`,
              borderRadius: 'var(--radius-md, 10px)',
              padding: '22px 18px',
              textAlign: 'center',
              cursor: 'pointer',
              background: dragging ? 'rgba(var(--accent-rgb, 59,130,246), 0.06)' : 'var(--bg-surface)',
              color: 'var(--text-secondary)',
            }}
          >
            <ArrowUpTrayIcon style={{ width: 26, height: 26, opacity: 0.7 }} />
            <div style={{ marginTop: 6, fontWeight: 600 }}>
              {fileName || 'Arrastra aquí un Excel (.xlsx/.xls) o CSV'}
            </div>
            <div className="list-hint" style={{ marginTop: 2 }}>
              máx. {schema.max_rows} filas · se lee en tu navegador, el fichero no se sube
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,.csv,text/csv"
              style={{ display: 'none' }}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) leerFichero(file);
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <button type="button" className="list-btn-secondary" onClick={descargarPlantilla}>
              <DocumentArrowDownIcon style={{ width: 15, height: 15 }} /> Descargar plantilla
            </button>
            <label className="list-hint" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                checked={updateExisting}
                onChange={(event) => setUpdateExisting(event.target.checked)}
              />
              Actualizar los que ya existan
            </label>
          </div>

          {headers.length > 0 && (
            <>
              <h5 style={{ margin: '16px 0 6px', fontSize: '0.9rem' }}>
                Columnas del fichero → campos de {schema.label.toLowerCase()}
              </h5>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                  gap: 8,
                  maxHeight: 260,
                  overflowY: 'auto',
                  paddingRight: 4,
                }}
              >
                {campos.map((field) => (
                  <label key={field.key} className="list-hint" style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span>
                      {field.label}
                      {field.required && <span style={{ color: 'var(--danger, #ef4444)' }}> *</span>}
                    </span>
                    <select
                      className="list-select"
                      value={mapping[field.key] || ''}
                      onChange={(event) => setMapping((prev) => ({ ...prev, [field.key]: event.target.value }))}
                    >
                      <option value="">— sin asignar —</option>
                      {headers.map((header) => (
                        <option key={header} value={header}>{header}</option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>

              {faltanObligatorios.length > 0 && (
                <p style={{ color: 'var(--warn, #f59e0b)', fontSize: '0.82rem', marginTop: 8 }}>
                  Falta por asignar: {faltanObligatorios.join(', ')}
                </p>
              )}

              {previewRows.length > 0 && (
                <>
                  <h5 style={{ margin: '14px 0 6px', fontSize: '0.9rem' }}>
                    Vista previa ({Math.min(previewRows.length, rows.length)} de {rows.length} filas)
                  </h5>
                  <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.74rem' }}>
                      <thead>
                        <tr>
                          {campos.filter((field) => mapping[field.key]).map((field) => (
                            <th key={field.key} style={{ textAlign: 'left', padding: '6px 8px', whiteSpace: 'nowrap' }}>
                              {field.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewRows.map((row, index) => (
                          <tr key={index} style={{ borderTop: '1px solid var(--border)' }}>
                            {campos.filter((field) => mapping[field.key]).map((field) => (
                              <td key={field.key} style={{ padding: '5px 8px', color: 'var(--text-secondary)' }}>
                                {row[field.key] || '—'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      {result && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <span className="list-badge list-badge--on">Creados: {result.created}</span>
            <span className="list-badge list-badge--accent">Actualizados: {result.updated}</span>
            <span className="list-badge list-badge--muted">Omitidos: {result.skipped}</span>
            <span className="list-badge list-badge--off">Errores: {(result.errors || []).length}</span>
          </div>
          {result.errors?.length > 0 && (
            <>
              <h5 style={{ margin: '8px 0 4px', fontSize: '0.86rem' }}>Filas con error</h5>
              <div style={{ maxHeight: 200, overflowY: 'auto', fontSize: '0.78rem' }}>
                {result.errors.map((row) => (
                  <div key={row.row} style={{ padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
                    <strong>Fila {row.row}:</strong> {row.message}
                  </div>
                ))}
              </div>
            </>
          )}
          {result.warnings?.length > 0 && (
            <>
              <h5 style={{ margin: '10px 0 4px', fontSize: '0.86rem' }}>Avisos</h5>
              <div style={{ maxHeight: 160, overflowY: 'auto', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                {result.warnings.map((row) => (
                  <div key={row.row} style={{ padding: '3px 0' }}>
                    <strong>Fila {row.row}:</strong> {row.message}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </Dialog>
  );
}
